// packages/front/src/portfolio/cash-balances-metric.tsx
//
// "Cash" summary tile with an info tooltip: the cash of every account by
// currency, converted to the display currency with the original in brackets.
// A negative balance (a broker margin loan) is listed too and marked as debt,
// so the lines add up to the total.

import { PORTFOLIO_UNKNOWN_ACCOUNT_ID, type PortfolioCashBalance } from "@signals/shared";
import { Info } from "lucide-react";
import { useId, useMemo, useState, type FC } from "react";
import { formatCurrency, formatNumber } from "../_common";

const UNKNOWN_ACCOUNT_NAME = "Unknown account";
/** Smallest balance that still shows as non-zero at two decimals; floating-point residue stays below it. */
const MIN_VISIBLE_CASH = 0.005;

export interface CashBalancesMetricProps {
  value: string;
  /** Undefined for saved historical snapshots, which keep only the total. */
  cashBalances: PortfolioCashBalance[] | undefined;
  displayCurrency: string;
  accountNames: ReadonlyMap<string, string>;
  /** Closed accounts are left out of the tooltip. */
  closedAccountIds: ReadonlySet<string>;
}

interface AccountCashGroup {
  accountId: string;
  name: string;
  balances: PortfolioCashBalance[];
}

function groupByAccount(
  cashBalances: PortfolioCashBalance[],
  accountNames: ReadonlyMap<string, string>,
): AccountCashGroup[] {
  const groups = new Map<string, AccountCashGroup>();
  for (const balance of cashBalances) {
    const group = groups.get(balance.accountId) ?? {
      accountId: balance.accountId,
      name: balance.accountId === PORTFOLIO_UNKNOWN_ACCOUNT_ID
        ? UNKNOWN_ACCOUNT_NAME
        : accountNames.get(balance.accountId) ?? balance.accountId,
      balances: [],
    };
    group.balances.push(balance);
    groups.set(balance.accountId, group);
  }
  return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export const CashBalancesMetric: FC<CashBalancesMetricProps> = ({
  value,
  cashBalances,
  displayCurrency,
  accountNames,
  closedAccountIds,
}) => {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  const groups = useMemo(
    () => (cashBalances
      ? groupByAccount(
          cashBalances.filter((balance) =>
            Math.abs(balance.amount) >= MIN_VISIBLE_CASH && !closedAccountIds.has(balance.accountId)),
          accountNames,
        )
      : []),
    [cashBalances, accountNames, closedAccountIds],
  );

  return (
    <div>
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        <span>Cash</span>
        <div
          className="relative inline-flex"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={(event) => {
            if (!event.currentTarget.contains(document.activeElement)) setOpen(false);
          }}
        >
          <button
            type="button"
            className="rounded-sm p-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Show cash by account"
            aria-describedby={open ? tooltipId : undefined}
            aria-controls={open ? tooltipId : undefined}
            aria-expanded={open}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                event.stopPropagation();
                setOpen(false);
              }
            }}
            onBlur={(event) => {
              const wrapper = event.currentTarget.parentElement;
              if (!wrapper || !(event.relatedTarget instanceof Node) || !wrapper.contains(event.relatedTarget)) {
                setOpen(false);
              }
            }}
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          {open && (
            <div
              id={tooltipId}
              role="tooltip"
              className="absolute left-0 top-full z-50 mt-1 w-72 max-w-[80vw] rounded-md border bg-white p-3 text-xs text-slate-900 shadow-lg dark:bg-slate-950 dark:text-slate-100"
            >
              {cashBalances === undefined ? (
                <p className="text-muted-foreground">
                  Cash by account is not stored in saved historical snapshots.
                </p>
              ) : groups.length === 0 ? (
                <p className="text-muted-foreground">No cash on open accounts.</p>
              ) : (
                <div className="space-y-2">
                  {groups.map((group) => (
                    <div key={group.accountId}>
                      <p className="font-medium">{group.name}</p>
                      <ul>
                        {group.balances.map((balance) => (
                          <li
                            key={`${balance.accountId}|${balance.currency}`}
                            className="flex justify-between gap-2"
                          >
                            <span className="text-muted-foreground">{balance.currency}</span>
                            <span className={balance.amount < 0 ? "text-red-600 dark:text-red-400" : undefined}>
                              {formatCurrency(balance.displayAmount, displayCurrency)}
                              {balance.currency !== displayCurrency && (
                                <span className={balance.amount < 0 ? undefined : "text-muted-foreground"}>
                                  {" "}({formatNumber(balance.amount)} {balance.currency})
                                </span>
                              )}
                              {balance.amount < 0 && " debt"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
};
